import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, MapPin, X, Loader2, Minus, Plus } from "lucide-react";
import { api, loadRazorpay } from "../lib/api";
import { ASSETS } from "../data/landing";
import { trackBeginCheckout, trackPurchase, trackViewEvent } from "../lib/analytics";

const fmtDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const fmtTime = (t) => {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  if (!hh || !mm) return t;
  const h = parseInt(hh, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = ((h + 11) % 12) + 1;
  return `${hr}:${mm} ${ampm}`;
};

const BookingModal = ({ event, onClose }) => {
  const navigate = useNavigate();
  const maxPax = event.allow_multiple ? Math.max(1, event.max_pax_per_booking || 10) : 1;
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", pax: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const total = (Number(event.amount_inr) * form.pax).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name.trim() || !form.phone.trim()) {
      setError("Full Name and Phone Number are required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        event_id: event.id,
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        pax: Number(form.pax),
      };
      trackBeginCheckout({ event, pax: payload.pax });
      const { data: order } = await api.post("/bookings/create-order", payload);
      const ok = await loadRazorpay();
      if (!ok) throw new Error("Could not load Razorpay");
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount_paise,
        currency: order.currency,
        name: "Hari Om Bol",
        description: order.event_title,
        order_id: order.razorpay_order_id,
        prefill: order.prefill,
        theme: { color: "#7B1113" },
        handler: async (resp) => {
          try {
            const { data: booking } = await api.post("/bookings/verify", {
              booking_id: order.booking_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            trackPurchase(booking);
            navigate(`/booking/${order.booking_id}`);
          } catch (err) {
            setError("Payment verification failed. Please contact support.");
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      });
      rzp.open();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Something went wrong");
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="booking-modal"
    >
      <div className="absolute inset-0 bg-[#2A1112]/60 backdrop-blur-sm" onClick={onClose} />
      <motion.form
        onSubmit={submit}
        initial={{ scale: 0.95, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl bg-[#FCFAF7] p-7 shadow-2xl"
      >
        <button type="button" onClick={onClose} className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#7B1113] shadow" aria-label="Close">
          <X className="h-5 w-5" />
        </button>
        <h3 className="text-xl font-semibold text-[#2A1112]">Book: {event.title}</h3>
        <p className="mt-1 text-sm text-[#5E4646]">₹{Number(event.amount_inr).toFixed(2)} per pax</p>

        <div className="mt-5 space-y-3">
          <input required placeholder="Full Name" value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} className="w-full rounded-xl border border-[#7B1113]/15 bg-white px-4 py-3 text-[#2A1112] outline-none focus:border-[#7B1113]" data-testid="book-name" />
          <input required placeholder="Phone Number" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full rounded-xl border border-[#7B1113]/15 bg-white px-4 py-3 text-[#2A1112] outline-none focus:border-[#7B1113]" data-testid="book-phone" />
          <input type="email" placeholder="Email (optional)" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full rounded-xl border border-[#7B1113]/15 bg-white px-4 py-3 text-[#2A1112] outline-none focus:border-[#7B1113]" data-testid="book-email" />
          <div className="flex items-center justify-between rounded-xl border border-[#7B1113]/15 bg-white px-4 py-2.5">
            <span className="text-sm font-medium text-[#5E4646]">Pax</span>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setForm(f => ({ ...f, pax: Math.max(1, f.pax - 1) }))} className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#7B1113]/10 text-[#7B1113] disabled:opacity-40" disabled={!event.allow_multiple || form.pax <= 1} data-testid="book-pax-minus">
                <Minus className="h-4 w-4" />
              </button>
              <span className="min-w-6 text-center text-base font-semibold text-[#2A1112]" data-testid="book-pax">{form.pax}</span>
              <button type="button" onClick={() => setForm(f => ({ ...f, pax: Math.min(maxPax, f.pax + 1) }))} className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#7B1113]/10 text-[#7B1113] disabled:opacity-40" disabled={!event.allow_multiple || form.pax >= maxPax} data-testid="book-pax-plus">
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between rounded-xl bg-[#7B1113]/5 px-4 py-3">
          <span className="text-sm font-medium text-[#5E4646]">Total</span>
          <span className="text-xl font-bold text-[#7B1113]" data-testid="book-total">₹{total}</span>
        </div>

        {error && <p className="mt-3 text-sm text-red-600" data-testid="book-error">{error}</p>}

        <button type="submit" disabled={loading} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#7B1113] px-6 py-3.5 text-sm font-semibold text-[#FCFAF7] shadow-lg transition hover:bg-[#8f1518] disabled:opacity-60" data-testid="book-submit">
          {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing…</> : `Pay ₹${total}`}
        </button>
      </motion.form>
    </motion.div>
  );
};

const BookCta = ({ onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="inline-flex w-fit items-center justify-center rounded-full bg-[#7B1113] px-6 py-3 text-sm font-semibold text-[#FCFAF7] shadow-lg transition hover:bg-[#8f1518]"
  >
    Book Ticket
  </button>
);

const EventSection = ({ ev, onBook }) => {
  const dateLine = [fmtDate(ev.event_date), fmtTime(ev.event_time)].filter(Boolean).join(" · ") || ev.event_at;
  return (
    <article data-testid={`event-card-${ev.id}`} className="overflow-hidden rounded-3xl bg-white shadow-[0_24px_48px_-16px_rgba(123,17,19,0.12)]">
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Left: poster + key info + top CTA */}
        <div className="lg:col-span-5 lg:border-r lg:border-[#7B1113]/8">
          <div className="relative aspect-[16/9] overflow-hidden bg-[#F6F2EA]">
            <img src={ev.poster_data_url} alt={ev.title} className="h-full w-full object-cover object-center" loading="lazy" />
          </div>
          <div className="p-6 md:p-7">
            <h2 className="text-2xl font-semibold tracking-tight text-[#2A1112] md:text-3xl">{ev.title}</h2>
            <div className="mt-3 space-y-1.5 text-sm text-[#5E4646]">
              {dateLine && <p className="flex items-center gap-2"><Calendar className="h-4 w-4" />{dateLine}</p>}
              {ev.venue && <p className="flex items-center gap-2"><MapPin className="h-4 w-4" />{ev.venue}</p>}
            </div>
            {ev.description && <p className="mt-4 text-[15px] leading-relaxed text-[#5E4646]">{ev.description}</p>}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <span className="text-2xl font-bold text-[#7B1113]">₹{Number(ev.amount_inr).toFixed(2)}<span className="ml-1 text-xs font-medium text-[#5E4646]">/pax</span></span>
              <BookCta onClick={() => onBook(ev)} testid={`book-btn-${ev.id}`} />
            </div>
          </div>
        </div>

        {/* Right: long description + end CTA */}
        <div className="lg:col-span-7">
          <div className="p-6 md:p-8 lg:p-10">
            <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-[#7B1113]">About this event</h3>
            {ev.long_description ? (
              <p className="mt-3 whitespace-pre-line text-[15px] leading-[1.75] text-[#2A1112] md:text-base" data-testid={`event-long-desc-${ev.id}`}>{ev.long_description}</p>
            ) : (
              <p className="mt-3 text-sm italic text-[#5E4646]">More details coming soon.</p>
            )}
            <div className="mt-8 flex items-center gap-4 border-t border-[#7B1113]/8 pt-6">
              <BookCta onClick={() => onBook(ev)} testid={`book-btn-bottom-${ev.id}`} />
              <span className="text-sm text-[#5E4646]">Secure your spot now</span>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default function EventBooking() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/events").then(r => setEvents(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openBooking = (ev) => {
    trackViewEvent(ev);
    setActive(ev);
  };

  return (
    <main className="min-h-screen bg-[#FCFAF7] px-6 py-16 md:px-12 md:py-24" data-testid="event-booking-page">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-center gap-4">
          <img src={ASSETS.logo} alt="Hari Om Bol" className="h-12 w-12 rounded-full object-cover" />
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-[#2A1112] md:text-4xl">Events</h1>
            <p className="text-sm text-[#5E4646] md:text-base">Book your ticket for upcoming Hari Om Bol events.</p>
          </div>
        </div>

        {loading ? (
          <div className="mt-16 flex items-center justify-center text-[#7B1113]"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : events.length === 0 ? (
          <div className="mt-16 rounded-3xl bg-white p-12 text-center shadow" data-testid="no-events">
            <p className="text-lg text-[#5E4646]">No events available right now. Please check back soon.</p>
          </div>
        ) : (
          <div className="mt-10 space-y-10">
            {events.map(ev => <EventSection key={ev.id} ev={ev} onBook={openBooking} />)}
          </div>
        )}
      </div>

      <AnimatePresence>
        {active && <BookingModal event={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </main>
  );
}
